import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, GitBranch, Code2, Server, Globe, Puzzle } from "lucide-react";
import { projectsData } from "../data/mock";
import { SectionReveal } from "./SectionReveal";

const typeIcons = {
  "Open Source": GitBranch,
  Enterprise: Server,
  Plugin: Puzzle,
  Community: Globe,
};

const typeAccent = {
  "Open Source": "border-emerald-200 dark:border-emerald-500/20 hover:border-emerald-300 dark:hover:border-emerald-500/40",
  Enterprise: "border-sky-200 dark:border-sky-500/20 hover:border-sky-300 dark:hover:border-sky-500/40",
  Plugin: "border-amber-200 dark:border-amber-500/20 hover:border-amber-300 dark:hover:border-amber-500/40",
  Community: "border-violet-200 dark:border-violet-500/20 hover:border-violet-300 dark:hover:border-violet-500/40",
};

const typeIconColor = {
  "Open Source": "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10",
  Enterprise: "text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10",
  Plugin: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10",
  Community: "text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10",
};

const ProjectCard = ({ project, index, isVisible }) => {
  const Icon = typeIcons[project.type] || Code2;
  const accent = typeAccent[project.type] || typeAccent["Open Source"];
  const iconColor = typeIconColor[project.type] || typeIconColor["Open Source"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotateX: 8 }}
      animate={isVisible ? { opacity: 1, y: 0, rotateX: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      className={`group bg-white dark:bg-zinc-900/50 border rounded-xl p-6 transition-colors duration-300 shadow-sm dark:shadow-none ${accent}`}
      style={{ perspective: 800 }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        {project.link && project.link !== "#" && (
          <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-zinc-400 dark:text-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors p-1">
            <ArrowUpRight className="w-4 h-4" />
          </a>
        )}
      </div>
      <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold text-lg mb-2 group-hover:text-zinc-950 dark:group-hover:text-white transition-colors">{project.title}</h3>
      <p className="text-zinc-500 text-sm leading-relaxed mb-5">{project.description}</p>
      <div className="flex flex-wrap gap-1.5">
        {project.technologies.map((tech) => (
          <span key={tech} className="text-xs px-2.5 py-1 bg-zinc-100 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/40 rounded-md text-zinc-600 dark:text-zinc-400 font-mono">{tech}</span>
        ))}
      </div>
    </motion.div>
  );
};

const ProjectsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="projects" className="relative py-28 lg:py-36 bg-white dark:bg-zinc-950 transition-colors duration-500" ref={sectionRef} aria-label="Featured Projects">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-zinc-300/60 dark:via-zinc-800/60 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionReveal animation="rotateIn">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-widest uppercase">Featured Work</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">Projects & contributions</h2>
            <p className="mt-4 text-zinc-500 text-lg max-w-2xl mx-auto">From WordPress Core and Gutenberg to enterprise solutions and community tools.</p>
          </div>
        </SectionReveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projectsData.map((project, i) => (
            <ProjectCard key={project.title} project={project} index={i} isVisible={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
